import {
  Component,
  DoCheck,
  EventEmitter,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import {
  DataEditHistoryResponse,
  DataTracingFieldHistoryAiResponse,
  DataTracingFieldHistoryAiTimelineEntry,
  DataTransportSummary,
  HistoryDetail, ValueType
} from '@features/data-tracing/models/data-edit-summary.model';
import { PlatformQuery, PlatformQueryResponse } from '@shared/models/platform-query/platform-query';
import { take } from 'rxjs';
import { PlatformQueryService } from '@core/services/platform-query.service';
import { PlatformCommand } from '@shared/commands/command-base';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CommandService } from '@core/services/command.service';
import { PlatformSidebarState } from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import { Store } from '@ngrx/store';
import { SetActivityFeedAction } from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import { UserInfo } from '@shared/models/user-info.model';
import {getClaims, getUserInfo} from '@shared/store/selectors/security.selector';
import { SecurityState } from '@shared/store/reducers/security.reducer';
import { ActivityFeedInfo, ActivityFeedType, ConversationsByFilter } from '@features/task-manager/collaboration/store/activity.model';
import { SnackBarService } from '@core/services/snack-bar.service';
import {DataContextFeedService} from '@shared/services/data-context-feed/data-context-feed.service';
import {DatePipe} from '@angular/common';
import {Api} from "@shared/constants/common-field.constants";
import {DataTracingService} from "@features/data-tracing/services/data-tracing.service";
import {ActivityRightSidebarStateService} from "@features/data-tracing/services/activity-right-sidebar-state.service";
import {ActivityComponents} from "@features/data-tracing/constants/activity-components";
import { FuseSplashScreenService } from '@barebone-fuse/@fuse/services/splash-screen.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';


export enum ContentScope {
  Default = 0,
  Company = 1,
  Project = 2,
  OrbitaxSupport = 3,
  TemporarySpace = 4,
  OrbitaxGlobal = 5,
  Public = 6,
  OrbitaxPublication = 7
}

@Component({
    standalone: false,
    selector: 'app-data-sources-field-history-details',
  templateUrl: './data-sources-field-history-details.component.html',
  styleUrls: ['./data-sources-field-history-details.component.scss'],
  encapsulation:ViewEncapsulation.None
})
export class DataSourcesFieldHistoryDetailsComponent implements OnInit, OnDestroy, OnChanges, DoCheck {
  @Input() public invalidate : boolean;
  @Input() public itemId: string;
  @Input() public fieldKey: string;
  @Input() public sourceType: string;
  @Input() public destinationType: string;
  @Input() public workbookId: string;
  @Input() public worksheetId: string;
  @Input() public cellColumnIndex: string;
  @Input() public cellRowIndex: string;
  @Input() public referenceCode: string; // This refers to DataTracingPageId
  @Output() public fieldHistoryEvent: EventEmitter<any> = new EventEmitter<any>();

  isMagicLinkUser = false;
  hasShareId = false;
  disableBtnHoverMessage = '<div><strong>This information is not available to view via the current workflow.</strong><div class="m-t-10">To view this information, please open the scenario directly from the solution menu and open the Data Tracing sidebar for the appropriate screen.</div></div>';

  public ValueType = ValueType;

  public dataEditSummaryId: string;
  public dataEditFieldDetails: DataEditHistoryResponse;

  /** Parsed AI output per docs/data-tracing-field-history-ai.md; set when integrating the LLM. */
  public fieldHistoryAiResponse: DataTracingFieldHistoryAiResponse | null = null;
  /** True when the XatBot query returned AI payload; does not alone switch the template (see showAiFieldHistoryView). */
  public isAiResponseAvailable = false;
  /** True while `XatBotDataTracingFieldHistoryQuery` is in flight (header shows loading affordance). */
  public isAiFieldHistoryPending = false;
  /** User opted into the AI summary layout (V2); default false so V1 shows when details load. */
  public showAiFieldHistoryView = false;

  /** Cached sort of `fieldHistoryAiResponse.timeline` for V2; refreshed when its reference changes. */
  public sortedAiTimeline: DataTracingFieldHistoryAiTimelineEntry[] = [];
  private lastFieldHistoryAiResponseRef: DataTracingFieldHistoryAiResponse | null = null;
  /** Row indices expanded in the AI timeline card; cleared when AI response reference changes. */
  private expandedAiTimelineIndices = new Set<number>();

  private fetchFromArchive: boolean = true;
  private showMoreHistory: boolean = true;

  private isLoading: boolean = false;
  private historyDaysConst: string =  '180';

  private static readonly timelineMissingValue = '\u2014';

  userInfo: UserInfo;
  conversationsByFilter: ConversationsByFilter = null;
  feedId = '';

  unSubscribe$ = new Subject<void>();

  constructor(
    private dataTracingService: DataTracingService,
    private platformQueryService: PlatformQueryService,
    private commandService: CommandService,
    private platformSidebarStore: Store<PlatformSidebarState>,
    private securityStore: Store<SecurityState>,
    private dataContextFeedService: DataContextFeedService,
    private snackBarService: SnackBarService,
    private activityRightSidebarStateService: ActivityRightSidebarStateService,
    private fuseSplashScreenService: FuseSplashScreenService
    ) {}

  ngOnInit(): void {
    this.activityRightSidebarStateService.setCurrentActivityRightSidebarComponent(ActivityComponents.DataTracingHistoryDetailsComponent);
    console.log('DataSourcesFieldHistoryComponent onInit');
    this.getEditFieldHistory();
    if (this.platformQueryService.platformLinkModel && this.platformQueryService.platformLinkModel.feedId) {
      this.feedId = this.platformQueryService.platformLinkModel.feedId;
    }
    this.securityStore.select(getUserInfo).pipe(takeUntil(this.unSubscribe$)).subscribe(userInfo => {
      this.userInfo = userInfo;
    });
    this.securityStore.select(getClaims).pipe(take(1)).subscribe(claims => {
      if (claims['MagicLinkId'] && claims['MagicLinkId'].length > 0) {
        this.isMagicLinkUser = true;
      }
    });
    if (this.platformQueryService?.platformLinkModel?.fieldValues['ShareId']) {
      this.hasShareId = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('DataSourcesFieldHistoryComponent Changes', changes);
    this.showMoreHistory = true;
    if (!changes['invalidate']?.isFirstChange()) {
      this.getEditFieldHistory();
      this.invalidate = false;
    }
  }
  private setDataEditSummaryId(dataEditSummaryId: string) {
    if (dataEditSummaryId && this.dataEditSummaryId !== dataEditSummaryId) {
      this.dataEditSummaryId = dataEditSummaryId;
    }
  }

  private addFieldValues(query: PlatformQuery) {

    query.fieldValues = {
      'DestinationType': this.destinationType,
      'SourceType': this.sourceType,
      'CellRowIndex': this.cellRowIndex,
      'CellColumnIndex': this.cellColumnIndex
    };

    if(this.worksheetId) {
      query.fieldValues['WorksheetId'] = this.worksheetId;
    }
    if(this.workbookId) {
      query.fieldValues['WorkbookId'] = this.workbookId;
    }
    if(this.itemId) {
      query.fieldValues['ItemId'] = this.itemId;
    }
    if(this.fieldKey) {
      query.fieldValues['FieldKey'] = this.fieldKey;
    }
    if (this.referenceCode) {
      query.fieldValues['ReferenceCode'] = this.referenceCode;
    }
    this.ensurePageId(query);
    query.setValue("FromArchive", this.fetchFromArchive);
    return query;
  }

  private ensurePageId(query: PlatformQuery) {
    const ref = query.getValue("ReferenceCode");
    const worksheetId = query.getValue("WorksheetId");
    const sourceType = query.fieldValues["SourceType"];
    if(!ref && !worksheetId && sourceType.includes("Entity")) {
      if (this.platformQueryService.platformLinkModel?.fieldValues?.DataEditSummaryId) {
        if (this.platformQueryService.platformLinkModel?.fieldValues?.DataEditSummaryId !== this.dataEditSummaryId){
          this.dataEditSummaryId = this.platformQueryService.platformLinkModel?.fieldValues?.DataEditSummaryId
          query.setValue("ReferenceCode", this.dataEditSummaryId);
        }
        else {
          query.setValue("ReferenceCode", this.dataEditSummaryId);
        }
      }
    }
  }

  private getEditFieldHistoryAiResponse() {
    this.isAiFieldHistoryPending = true;
    const query = new PlatformQuery();
    query.api = Api.XatBotApi;
    query.name = 'XatBotDataTracingFieldHistoryQuery';
    query.projectId = '[PROJECT_ID]';

    this.addFieldValues(query);
    query.setValue('TransportId', this.dataEditFieldDetails?.transportSummary?.transportId);
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      const aiResponse = response.fieldValues['FieldHistoryAiResponse'] as DataTracingFieldHistoryAiResponse | undefined;
      this.fieldHistoryAiResponse = aiResponse ?? null;
      this.isAiResponseAvailable = !!aiResponse;
      this.isAiFieldHistoryPending = false;
    });
  }

  private getEditFieldHistory() {
    this.isLoading = true;
    this.isAiResponseAvailable = false;
    this.isAiFieldHistoryPending = false;
    this.showAiFieldHistoryView = false;

    const query = new PlatformQuery();
    query.api = Api.Collaboration;
    query.name = 'DataTracingFieldHistoryQuery';
    query.projectId = '[PROJECT_ID]';
    this.addFieldValues(query);
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
        this.dataEditFieldDetails = response.fieldValues['DataEditHistoryResponse'] as DataEditHistoryResponse;
        this.setDataEditSummaryId(response.fieldValues['DataEditSummaryId'] as string);

        this.getEditFieldHistoryAiResponse();

        this.fieldHistoryEvent.emit({
          type: 'DataEditFieldHistoryReceived',
          data: {
            DataEditSummaryId: this.dataEditSummaryId,
            DataEditSummaryTitle: response.fieldValues['SummaryTitle']
          }
        });
      this.isLoading = false;
        if (!this.dataEditFieldDetails?.editHistories?.length) {
          return;
        }
        for (const editHistory of this.dataEditFieldDetails.editHistories) {
          const historyDetails = editHistory.historyDetails?.sort((a, b) => a.order - b.order);
          if (!historyDetails?.length) {
            continue;
          }
          for (let i = 0; i < historyDetails.length; i++) {
            historyDetails[i] = this.getProcessedHistoryDetail(historyDetails[i]);
          }
        }
      });
  }

  private getProcessedHistoryDetail(historyDetail: HistoryDetail): HistoryDetail {
    switch (historyDetail.valueType) {
      case ValueType.DateTime:
        historyDetail.styleClass = 'date';
        const datePipe = new DatePipe('en-US');
        historyDetail.value = datePipe.transform(historyDetail.value, 'MM/dd/yyyy') + ' at ' + datePipe.transform(historyDetail.value, 'h:mm a (UTC)');;
        break;
      case ValueType.Text:
        historyDetail.styleClass = 'file-details';
        break;
      case ValueType.Url:
        historyDetail.styleClass = 'file-details';
        historyDetail.isUrl = true;
        break;
    }
    if (historyDetail.title && historyDetail.title.length > 0) {
      historyDetail.title = historyDetail.title + ': ';
    } else {
      historyDetail.title = '';
    }
    return historyDetail;
  }

  onClickBack() {
    console.log('DataSourcesFieldHistoryComponent BackClicked');
    this.fieldHistoryEvent.emit({
        type: 'BackClicked',
        data: {
          'DataEditSummaryId': this.getDataEditSummaryId(),
          'SourceType': this.sourceType
        }
      }
    );
  }

  private getDataEditSummaryId() {
    if (this.dataEditSummaryId) {
      return this.dataEditSummaryId;
    }
    if (this.workbookId) {
      return this.workbookId;
    }
    if (this.sourceType) {
      return this.sourceType;
    }
    return '';
  }

  onNavigateToSource(summary: DataTransportSummary) {
    console.log('summary:', summary);
    this.dataTracingService.navigateToSource(summary);
  }

  ngOnDestroy(): void {
    this.activityRightSidebarStateService.unloadActivityRightSidebarComponent(ActivityComponents.DataTracingHistoryDetailsComponent);
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  onAddComment() {
    const feedInfo = new ActivityFeedInfo({
      type: ActivityFeedType.DataContextFeed,
      id: this.feedId,
      isDefault: true,
      title: this.dataContextFeedService.currentDataContextFeed.title,
      isUserAction: true,
      conversationId : this.dataEditFieldDetails.conversationId,
      additionReferenceCodes: [this.feedId]
    });

    this.fieldHistoryEvent.emit({
      type: 'AddCommentClicked',
      data : feedInfo
    });
  }
  public getHistoryDaysTitle():string{
    return "Full History"
  }

  /** True when V2 (AI summary) should replace the classic details/history view. */
  get showAiFieldHistoryV2(): boolean {
    return !!(
      this.dataEditFieldDetails &&
      this.showAiFieldHistoryView &&
      this.isAiResponseAvailable &&
      this.fieldHistoryAiResponse
    );
  }

  openAiFieldHistoryView(): void {
    if (this.isAiResponseAvailable && this.fieldHistoryAiResponse) {
      this.showAiFieldHistoryView = true;
    }
  }

  closeAiFieldHistoryView(): void {
    this.showAiFieldHistoryView = false;
  }

  /** Timeline entries sorted by `order` for V2 rendering. */
  private computeSortedAiTimeline(): DataTracingFieldHistoryAiTimelineEntry[] {
    if (!this.fieldHistoryAiResponse?.timeline?.length) {
      return [];
    }
    return [...this.fieldHistoryAiResponse.timeline].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  }

  ngDoCheck(): void {
    if (this.fieldHistoryAiResponse !== this.lastFieldHistoryAiResponseRef) {
      this.lastFieldHistoryAiResponseRef = this.fieldHistoryAiResponse;
      this.sortedAiTimeline = this.computeSortedAiTimeline();
      this.expandedAiTimelineIndices.clear();
    }
  }

  isAiTimelineExpanded(index: number): boolean {
    return this.expandedAiTimelineIndices.has(index);
  }

  toggleAiTimelineExpanded(index: number): void {
    if (this.expandedAiTimelineIndices.has(index)) {
      this.expandedAiTimelineIndices.delete(index);
    } else {
      this.expandedAiTimelineIndices.add(index);
    }
  }

  /** Show the value-change row when at least one side is present. */
  hasTimelineValueChange(entry: DataTracingFieldHistoryAiTimelineEntry): boolean {
    return this.hasTimelineSideValue(entry.valueBefore) || this.hasTimelineSideValue(entry.valueAfter);
  }

  hasTimelineSideValue(value: string | null | undefined): boolean {
    return value != null && value !== '';
  }

  getTimelineValueFromDisplay(entry: DataTracingFieldHistoryAiTimelineEntry): string {
    return this.hasTimelineSideValue(entry.valueBefore) ? entry.valueBefore! : DataSourcesFieldHistoryDetailsComponent.timelineMissingValue;
  }

  getTimelineValueToDisplay(entry: DataTracingFieldHistoryAiTimelineEntry): string {
    return this.hasTimelineSideValue(entry.valueAfter) ? entry.valueAfter : DataSourcesFieldHistoryDetailsComponent.timelineMissingValue;
  }

}
